import { LinkedList, ConsLinkedList, Option } from 'prelude-ts';

/**
 * Types that support equality checking against a specific type.
 */
export interface HasEquals<T> {
  equals(that: T): boolean;
}

/**
 * Immutable data structure for maintaining undo/redo history.
 */
class History<T> {
  private readonly past: LinkedList<T>;
  private readonly present: T;
  private readonly future: LinkedList<T>;

  /** Total number of times history has been changed. */
  private readonly _length: number;

  private constructor(
    past: LinkedList<T>,
    present: T,
    future: LinkedList<T>,
    length: number
  ) {
    this.past = past;
    this.present = present;
    this.future = future;
    this._length = length;
  }

  /**
   * Construct a `History` with a single current value but
   * no undo/redo history.
   *
   * @param initial first entry in the history
   */
  static of<T>(initial: T): History<T> {
    return new History(LinkedList.empty(), initial, LinkedList.empty(), 0);
  }

  /**
   * Get the current value.
   */
  get(): T {
    return this.present;
  }

  getHistory(): LinkedList<T> {
    return new ConsLinkedList(this.present, this.past);
  }

  /**
   * Set the current value to the given value. The previous current is
   * added to the undo history; the redo history is cleared.
   *
   * @param value value to set current to
   */
  set(value: T): History<T> {
    const newPast = new ConsLinkedList(this.present, this.past);
    return new History(newPast, value, LinkedList.empty(), this._length + 1);
  }

  /**
   * Set the current value similar to `set`; except if the new value
   * equals the current, do nothing. Additionally, if the new value
   * equals the next value in history, then redo instead of clearing
   * redo history.
   *
   * @param value new current value
   */
  setDeduplicate(value: T & HasEquals<T>): History<T> {
    if (value.equals(this.present)) {
      return this;
    }

    const next = this.next();
    if (next.isSome() && value.equals(next.get())) {
      return this.redo();
    }

    return this.set(value);
  }

  /**
   * Previous value in the history, if any. This is what the current
   * value would become upon an undo.
   */
  previous(): Option<T> {
    return this.past.head();
  }

  /**
   * Next value in the history, if any. This is what the current
   * value would become upon a redo.
   */
  next(): Option<T> {
    return this.future.head();
  }

  /**
   * Go back one step in the history, if possible.
   * Nothing happens if there is no undo history.
   */
  undo(): History<T> {
    if (this.past.isEmpty()) {
      return this;
    }
    const newFuture = new ConsLinkedList(this.present, this.future);
    return new History(
      this.past.tail().get(),
      this.past.head().get(),
      newFuture,
      this._length
    );
  }

  /**
   * Go forward one step in the history, if possible.
   * Nothing happens if there is no undo history.
   */
  redo(): History<T> {
    if (this.future.isEmpty()) {
      return this;
    }
    const newPast = new ConsLinkedList(this.present, this.past);
    return new History(
      newPast,
      this.future.head().get(),
      this.future.tail().get(),
      this._length
    );
  }

  /**
   * True if the history has a past.
   */
  hasUndo(): boolean {
    return !this.past.isEmpty();
  }

  /**
   * True if the history has a future.
   */
  hasRedo(): boolean {
    return !this.future.isEmpty();
  }

  /**
   * Number of times history has been modified.
   * Specifically, this is the number of times `set` has been called.
   *
   * Undo and redo do not change history length. `setDeduplicate`
   * only increases length if does not deduplicate.
   */
  length(): number {
    return this._length;
  }
}

export default History;
